import { put, select, takeLatest } from "redux-saga/effects";
import { signup } from "../../lib/services";
import flowStages from "../../flowStages";
import actiontypes from "../actions/actiontypes";
import {
  emailAutoLinkNeeded,
  handleError,
  loginSetFlowStageWatcher,
  loginWithPasswordWatcher,
  setEmail,
  setSignupFailure,
  setSignupSuccess,
  setTranslatableErrorMessage,
  setUsername,
} from "../actions/loginactioncreators";
import getClientId from "../helpers/getClientId";
import getLocale from "../helpers/getLocale";
import { defineMessage } from "react-intl";

const flowStageMessageMap = {
  [flowStages.SIGNUP_ALREADY_SIGNED_UP]: defineMessage({
    id: "signup.alreadySignedUp",
    defaultMessage: "An account with this email already exists. If you previously signed in through a different platform, please use that method to continue. Need help? Contact support at {helpLink}.",
    description: "Sign-up flow message shown when user has already completed sign-up",
  }),
  [flowStages.SIGNUP_VALID]: defineMessage({
    id: "signupValid",
    defaultMessage: "Signup is valid. You can now sign in.",
    description: "Sign-up flow message shown when migration sign-up checks are successful",
  }),
  [flowStages.SIGNUP_EMAIL_EXISTS]: defineMessage({
    id: "signup.emailExists",
    defaultMessage: "It looks like you already have an account. Try signing in instead, or check if you linked your account through a different platform such as ScreeningDirect.",
    description: "Sign-up flow message shown when the provided email already exists in the system",
  }),
  [flowStages.SIGNUP_SSO_REQUIRED]: defineMessage({
    id: "signup.ssoRequiredBlurb",
    defaultMessage: "Your email address is associated with an organization that uses Single Sign-On (SSO). To continue, please click the \"Return to Sign In\" button and login using your company provided credentials. You do not need to complete signup.",
    description: "Sign-up flow message shown when the user must authenticate through SSO",
  }),
};

export function* signupAsync(action) {
  try {
    const clientId = yield select(getClientId);
    const locale = yield select(getLocale);
    const payload = {
      client_id: clientId,
      username: action.payload.username?.trim(),
      password: action.payload.password?.trim(),
      accountNumber: action.payload.accountNumber?.trim(),
      email: action.payload.email?.trim().toLowerCase(),
      newEmailRequired: action.payload.newEmailRequired,
    };
    const options = {
      headers: {
        "Accept-Language": locale,
      },
    };

    let { data } = yield signup(payload, options);
    let flowStage = data?.flowStage;

    switch (flowStage) {
      case flowStages.SIGNUP_ALREADY_SIGNED_UP:
      case flowStages.SIGNUP_EMAIL_EXISTS:
      case flowStages.SIGNUP_PLATFORM_EMAIL_EXISTS:
        yield put(setSignupSuccess({ flowStage, message: flowStageMessageMap[flowStage], returnToLogin: false }));
        yield put(loginSetFlowStageWatcher({ flowStage: "SIGNUP_ALREADY_SIGNED_UP" }));
        return;
      case flowStages.SIGNUP_SSO_REQUIRED:
        yield put(setSignupSuccess({ flowStage, message: flowStageMessageMap[flowStage], returnToLogin: false }));
        yield put(loginSetFlowStageWatcher({ flowStage: "SIGNUP_SSO_REQUIRED" }));
        return;
      case flowStages.SIGNUP_VALID:
        if (data?.email && typeof data.email === "string" && data.email.trim().length > 0) {
          yield put(setEmail(data.email));
          yield put(setSignupSuccess({ flowStage, message: "", returnToLogin: false }));
          yield put(emailAutoLinkNeeded());
          yield put(loginWithPasswordWatcher({ ...payload, email: data.email, flowParameter: "signup" }));
          return;
        } else {
          throw new Error(`Expected signup response to include email for flowStage ${flowStages.SIGNUP_VALID}`);
        }
      case "LOGIN":
        yield put(setUsername(payload.username));
        yield put(setSignupSuccess({ flowStage, message: "", returnToLogin: true }));
        yield put(loginSetFlowStageWatcher({ flowStage: "LOGIN" }));
        return;
      case flowStages.SIGNUP_NO_EMAIL:
        yield put(setSignupSuccess({ flowStage, message: "", returnToLogin: false }));
        yield put(loginSetFlowStageWatcher({ flowStage: "SIGNUP_NO_EMAIL" }));
        return;
      default:
        if (flowStageMessageMap[flowStage]) {
          yield put(
            setSignupSuccess({
              flowStage,
              message: flowStageMessageMap[flowStage],
              returnToLogin: false,
            })
          );
          yield put(setTranslatableErrorMessage(flowStageMessageMap[flowStage]));
          return;
        }
        throw new Error(`invalid flowStage ${flowStage}`);
    }
  } catch (error) {
    yield put(
      setSignupFailure({
        message: "",
        flowStage: "SIGNUP_ERROR",
      })
    );
    yield put(handleError(error));
  }
}

export function* watchSignupAsync() {
  yield takeLatest(actiontypes.USER_SIGNUP, signupAsync);
}